import { Injectable, signal } from '@angular/core';

export type ToastSeverity = 'error' | 'warning' | 'success' | 'info';

export interface Toast {
  /** Unique id so the template can trackBy and the service can dismiss one. */
  id: number;
  severity: ToastSeverity;
  /** Optional bold heading. When omitted a severity default is shown. */
  summary?: string;
  /** Main message body. */
  detail: string;
}

/**
 * App-wide toast notifications. Replaces the native `alert()` (which can't be
 * themed and blocks the page) with non-blocking, theme-aware toasts rendered by
 * a single <app-toast> mounted at the app root. Colours come from the --wg-*
 * tokens, so toasts follow light/dark automatically.
 *
 * Usage: inject ToastService and call `toast.error('…')` / `warn` / `success` /
 * `info`. Each toast auto-dismisses after `DEFAULT_LIFE_MS` (errors linger a
 * little longer) and can be dismissed early via the close button.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  /** Active toasts, newest last. Read by ToastComponent. */
  readonly toasts = signal<Toast[]>([]);

  private _idCounter = 0;
  private readonly _timers = new Map<number, ReturnType<typeof setTimeout>>();

  private static readonly DEFAULT_LIFE_MS = 4500;
  /** Errors stay a bit longer so the user can read what went wrong. */
  private static readonly ERROR_LIFE_MS = 6000;

  error(detail: string, summary?: string): void {
    this.show('error', detail, summary, ToastService.ERROR_LIFE_MS);
  }

  warn(detail: string, summary?: string): void {
    this.show('warning', detail, summary);
  }

  success(detail: string, summary?: string): void {
    this.show('success', detail, summary);
  }

  info(detail: string, summary?: string): void {
    this.show('info', detail, summary);
  }

  show(
    severity: ToastSeverity,
    detail: string,
    summary?: string,
    life: number = ToastService.DEFAULT_LIFE_MS,
  ): void {
    const id = ++this._idCounter;
    this.toasts.update((list) => [...list, { id, severity, summary, detail }]);
    const handle = setTimeout(() => this.dismiss(id), life);
    this._timers.set(id, handle);
  }

  dismiss(id: number): void {
    const handle = this._timers.get(id);
    if (handle) {
      clearTimeout(handle);
      this._timers.delete(id);
    }
    this.toasts.update((list) => list.filter((t) => t.id !== id));
  }
}
